function deserializeHTMLToSlate(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const isTextNode = (node) => node.nodeType === 3;
  const isElementNode = (node) => node.nodeType === 1;

  const autoLinkText = (text) => {
    text = text.replace(/\u200B/g, ""); // Remove zero-width space
    if (!text.trim()) return null;

    const parts = [];
    const regex = /(\s|^)(https?:\/\/[^\s]+)|(@\w+)|(#\w+)/gi;
    let lastIndex = 0;

    for (const match of text.matchAll(regex)) {
      const [fullMatch, , url, mention, hashtag] = match;
      const index = match.index;

      if (index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, index) });
      }

      if (url) {
        parts.push({ text: url, link: url });
      } else if (mention) {
        parts.push({ text: mention, mention: true });
      } else if (hashtag) {
        parts.push({ text: hashtag, hashtag: true });
      } else {
        parts.push({ text: fullMatch });
      }

      lastIndex = index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex) });
    }

    return parts.length ? parts : [{ text }];
  };

  const deserializeNode = (node) => {
    if (isTextNode(node)) {
      return autoLinkText(node.textContent);
    }

    if (!isElementNode(node)) return null;

    const children = Array.from(node.childNodes)
      .map(deserializeNode)
      .flat()
      .filter(Boolean);

    const nodeName = node.nodeName.toUpperCase();

    switch (nodeName) {
    case "TABLE":
      return {
        type: "table",
        children: Array.from(node.querySelectorAll("tr")).map(deserializeNode)
      };
    case "TR":
      return {
        type: "table-row",
        children: Array.from(node.children).map(deserializeNode)
      };
    case "TH":
      return {
        type: "table-header-cell",
        children: children.length ? children : [{ text: "" }]
      };
    case "TD":
      return {
        type: "table-cell",
        children: children.length ? children : [{ text: "" }]
      };

    case "UL":
      return { type: "bulleted-list", children };
    case "OL":
      return { type: "numbered-list", children };
    case "LI": {
      const nestedList = children.find(
        (c) => c?.type === "bulleted-list" || c?.type === "numbered-list"
      );
      const content = children.filter(
        (c) => c?.type !== "bulleted-list" && c?.type !== "numbered-list"
      );

      const listItem = {
        type: "list-item",
        children: content.length ? content : [{ text: "" }]
      };

      return nestedList ? [listItem, nestedList] : listItem;
    }

    case "H1": return { type: "h1", children };
    case "H2": return { type: "h2", children };
    case "H3": return { type: "h3", children };
    case "H4": return { type: "h4", children };
    case "H5": return { type: "h5", children };
    case "H6": return { type: "h6", children };

    case "BLOCKQUOTE":
      return { type: "block-quote", children };

    case "PRE":
      return { type: "code-block", children: children.length ? children : [{ text: "" }] };
    case "CODE":
      return children.map(child => ({ ...child, code: true }));

    case "STRONG":
    case "B":
      return children.map(child => ({ ...child, bold: true }));
    case "EM":
    case "I":
      return children.map(child => ({ ...child, italic: true }));
    case "U":
      return children.map(child => ({ ...child, underline: true }));
    case "DEL":
    case "S":
      return children.map(child => ({ ...child, strikethrough: true }));

    case "SPAN": {
      const style = node.getAttribute("style") || "";
      const colorMatch = style.match(/color:\s*(#[0-9a-fA-F]+|[a-zA-Z]+)/);
      const bgMatch = style.match(/background-color:\s*(#[0-9a-fA-F]+|[a-zA-Z]+)/);
      return children.map(child => ({
        ...child,
        ...(colorMatch ? { color: colorMatch[1] } : {}),
        ...(bgMatch ? { backgroundColor: bgMatch[1] } : {})
      }));
    }

    case "A": {
      const href = node.getAttribute("href");
      return children.map(child => ({ ...child, link: href }));
    }

    case "IMG":
      return { type: "image", url: node.getAttribute("src"), children: [{ text: "" }] };

    case "IFRAME":
      return { type: "embed", url: node.getAttribute("src"), children: [{ text: "" }] };

    case "VIDEO":
      return { type: "video", url: node.getAttribute("src"), children: [{ text: "" }] };

    case "BR":
      return { text: "\n" };

    case "DIV": {
      const childTags = Array.from(node.children).map(n => n.tagName);
      if (childTags.every(tag => tag === "LI")) {
        return { type: "bulleted-list", children };
      }
      if (childTags.every(tag => tag === "OL")) {
        return { type: "bulleted-list", children };
      }
      return {
        type: "paragraph",
        children: children.length ? children : [{ text: "" }]
      };
    }

    case "P": {
      if (node.parentElement?.tagName === "LI") return children;
      if (node.parentElement?.tagName === "OL") return children;
      if (children.every(c => c.text === "")) return null;
      return {
        type: "paragraph",
        children: children.length ? children : [{ text: "" }]
      };
    }

    default:
      return {
        type: "paragraph",
        children: children.length ? children : [{ text: "" }]
      };
    }
  };

  const bodyNodes = Array.from(doc.body.childNodes);
  const parsed = bodyNodes.map(deserializeNode).flat().filter(Boolean);

  // Normalize: Group orphan list-items
  const normalized = [];
  for (const node of parsed) {
    if (Array.isArray(node)) {
      normalized.push(...node);
    } else if (node.type === "list-item") {
      const last = normalized[normalized.length - 1];
      if (!last || !["bulleted-list", "numbered-list"].includes(last.type)) {
        normalized.push({ type: "bulleted-list", children: [node] });
      } else {
        last.children.push(node);
      }
    } else {
      normalized.push(node);
    }
  }

  return normalized;
}

export default deserializeHTMLToSlate;
